// Action creator
import { MruItem } from "@ms/planner-models/lib/local/client/mru/MruItem";
import { Bucket } from "@ms/planner-models/lib/local/client/planner/bucket/Bucket";
import { CustomFieldDefinition } from "@ms/planner-models/lib/local/client/planner/customFields/CustomFieldDefinition";
import { QueryRelationship, SingleEntityQueryType } from "@ms/planner-models/lib/local/client/planner/EntityRelationships";
import { EntityType } from "@ms/planner-models/lib/local/client/planner/EntityType";
import { Plan } from "@ms/planner-models/lib/local/client/planner/plan/Plan";
import { Task } from "@ms/planner-models/lib/local/client/planner/task/Task";
import { TodoSettings } from "@ms/planner-models/lib/local/client/todo/TodoSettings/TodoSettings";
import { parseTrouterSubscriptionResponse } from "@ms/planner-models/lib/local/utilities/TrouterUtilities";
// Actions
import { FetchTodoSettingsSucceededAction } from "@ms/planner-actions/lib/local/appContext/FetchTodoSettingsAction";
import { FetchEntityCollectionSucceededAction, FetchEntitySucceededAction } from "@ms/planner-actions/lib/local/EntityActions";
import { FetchMruItemsSucceededAction } from "@ms/planner-actions/lib/local/mru/MruActions";
import { FetchTeamsTasksTabsSucceededAction } from "@ms/planner-actions/lib/local/teamsTasksTabs/FetchTeamsTasksTabsActions";
import { TrouterSubscriptionFailedAction, TrouterSubscriptionSuccessAction } from "@ms/planner-actions/lib/local/trouter/TrouterActions";
import { TraceLevel } from "@ms/uno-telemetry/lib/local/events/Trace.event";
// Utilities
import { getPlanRouteDetailsForTab } from "@ms/uno-routing/lib/local/utilities/TeamsTabParsingUtilities";
import { Store } from "@ms/planner-stores/lib/local/stores/Store";
// Constants
import { PlanType } from "@ms/uno-constants/lib/local/AppConstants";
import { PreFetchedDataKeys } from "@ms/uno-constants/lib/local/DataPreFetcherConstants";
/**
 * Action creator storing the pre-fetched data
 */ export class DataPreFetchActionCreator {
    storePrefetchedServiceResult(result) {
        if (result.errorMessage) {
            this.loggers.traceLogger.logTrace(0x1e150560 /* tag_4fqv6 */ , TraceLevel.Warning, `PreFetcher failed: [Type:${result.type}][Message:${result.errorMessage}]`);
            return;
        }
        switch(result.type){
            case PreFetchedDataKeys.Mru:
                this.storePreFetchedMruData(result.data);
                break;
            case PreFetchedDataKeys.MyDayTasks:
                this.storePreFetchedMyDayTasksData(result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.ATMTasks:
                this.storePreFetchedATMTasksData(result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.Plan:
                this.storePreFetchedPlanData(result.data, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.TTTabs:
                this.storePreFetchedTTTabsData(result.data);
                break;
            case PreFetchedDataKeys.Buckets:
                this.storePreFetchedBucketsData(result.planId, result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.BasicPlanTasks:
                this.storePreFetchedPlanTasksData(result.planId, result.data, result.nextLink, result.deltaLink, result.trouterViewUpdateKey);
                break;
            case PreFetchedDataKeys.TodoSettings:
                this.storePreFetchedTodoSettings(result.data);
                break;
            case PreFetchedDataKeys.CustomFieldDefinitions:
                this.storePreFetchedCustomFieldDefinitionsData(result.planId, result.data, result.deltaLink, result.trouterViewUpdateKey);
                break;
            default:
                this.loggers.traceLogger.logTrace(0x1e15055f /* tag_4fqv5 */ , TraceLevel.Warning, `Unsupported pre-fetched data type to save`);
                break;
        }
    }
    /**
     * Stores the pre-fetched MRU data
     * @param data: The pre-fetched MRU data
     */ storePreFetchedMruData(data) {
        const mruItems = [];
        data.map((item)=>MruItem.fromResource(item)).filter((mruItem)=>mruItem != null).forEach((mruItem)=>mruItems.push(mruItem));
        this.dispatcher.dispatchActionAsync(new FetchMruItemsSucceededAction(mruItems));
    }
    /**
     * Stores the pre-fetched MyDay tasks data
     * @param data: The pre-fetched MyDay tasks data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedMyDayTasksData(data, nextLink, deltaLink, trouterViewUpdateKey) {
        const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
        this.dispatchFetchedEntityCollection(this.accumulatedMyDayTasks, data, (resource)=>Task.fromResource(resource), EntityType.Task, EntityType.User, userId, QueryRelationship.TasksInMyDayOfUser, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched ATM tasks data
     * @param data: The pre-fetched ATM tasks data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedATMTasksData(data, nextLink, deltaLink, trouterViewUpdateKey) {
        const userId = this.storeLoader.load(Store.AppContext).getCurrentUserIdentitySet().user.id;
        this.dispatchFetchedEntityCollection(this.accumulatedAtmTasks, data, (resource)=>Task.fromResource(resource), EntityType.Task, EntityType.User, userId, QueryRelationship.TasksAssignedToUser, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched plan data
     * @param data: The pre-fetched plan data
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedPlanData(data, trouterViewUpdateKey) {
        for (const plan of data){
            this.dispatcher.dispatchActionAsync(new FetchEntitySucceededAction(EntityType.Plan, Plan.fromResource(plan)));
            // Dispatch Trouter subscription result for each plan
            this.dispatchTrouterSubscriptionResult(trouterViewUpdateKey, plan.id, SingleEntityQueryType.PlanById);
        }
    }
    /**
     * Stores the pre-fetched TT tabs data
     * @param data: The pre-fetched TT tabs data
     */ storePreFetchedTTTabsData(data) {
        const tabs = data.map((tabResource)=>{
            const details = getPlanRouteDetailsForTab(tabResource.id, tabResource.tabContentUrl) ?? {
                planId: `invalidEntityId${tabResource.id}`,
                planType: PlanType.Basic
            };
            return {
                ...tabResource,
                tabName: decodeURIComponent(tabResource.tabName),
                entityId: details.planId,
                app: details.planType,
                orgId: details.planType === PlanType.Premium ? details.orgId : null
            };
        }) ?? [];
        this.dispatcher.dispatchActionAsync(new FetchTeamsTasksTabsSucceededAction(tabs));
    }
    /**
     * Stores the pre-fetched buckets data
     * @param planId: The plan id to which the buckets belong
     * @param data: The pre-fetched buckets data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedBucketsData(planId, data, nextLink, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection(this.accumulatedBuckets, data, (resource)=>Bucket.fromResource(resource), EntityType.Bucket, EntityType.Plan, planId, QueryRelationship.BucketsInPlan, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched tasks data for a basic plan
     * @param planId: The plan id to which the tasks belong
     * @param data: The pre-fetched tasks data
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedPlanTasksData(planId, data, nextLink, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection(this.accumulatedPlanTasks, data, (resource)=>Task.fromResource(resource), EntityType.Task, EntityType.Plan, planId, QueryRelationship.TasksInPlan, nextLink, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Stores the pre-fetched custom field definitions data
     * @param planId: The plan id to which the custom field definitions belong
     * @param data: The pre-fetched custom field definitions data
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ storePreFetchedCustomFieldDefinitionsData(planId, data, deltaLink, trouterViewUpdateKey) {
        this.dispatchFetchedEntityCollection([], data, (resource)=>CustomFieldDefinition.fromResource(resource), EntityType.CustomFieldDefinition, EntityType.Plan, planId, QueryRelationship.CustomFieldDefinitionsInPlan, undefined, deltaLink, trouterViewUpdateKey);
    }
    /**
     * Converts raw resources, accumulates entities into the provided array, dispatches a FetchEntityCollectionSucceededAction,
     * and dispatches the Trouter subscription result.
     * @param accumulator: The running accumulator array (mutated in place)
     * @param resourceData: The raw resource data to convert
     * @param fromResource: Converter function from resource to client entity
     * @param entityType: The type of the entities in the collection
     * @param parentEntityType: The type of the parent entity
     * @param parentEntityId: The ID of the parent entity
     * @param queryRelationship: The query relationship for this collection
     * @param nextLink: The next link for pagination
     * @param deltaLink: The delta link for tracking changes
     * @param trouterViewUpdateKey: The Trouter subscription result
     */ dispatchFetchedEntityCollection(accumulator, resourceData, fromResource, entityType, parentEntityType, parentEntityId, queryRelationship, nextLink, deltaLink, trouterViewUpdateKey) {
        accumulator.push(...resourceData.map(fromResource));
        this.dispatcher.dispatchActionAsync(new FetchEntityCollectionSucceededAction(entityType, parentEntityType, parentEntityId, queryRelationship, accumulator, nextLink, true, accumulator.map((entity)=>entity.id), deltaLink));
        // Dispatch Trouter subscription result for the collection
        this.dispatchTrouterSubscriptionResult(trouterViewUpdateKey, parentEntityId, queryRelationship);
    }
    /**
     * Stores the pre-fetched todo settings data
     * @param data: The pre-fetched settings data
     */ storePreFetchedTodoSettings(data) {
        const succeededAction = new FetchTodoSettingsSucceededAction(new TodoSettings(data));
        this.dispatcher.dispatchActionAsync(succeededAction);
    }
    /**
     * Dispatches the appropriate Trouter subscription action based on the response.
     * @param trouterViewUpdateKey The Trouter view update key from the response header
     * @param scopeEntityId The scope entity ID (parent for collections, entity itself for single)
     * @param queryType The query type (collection relationship or single entity type)
     */ dispatchTrouterSubscriptionResult(trouterViewUpdateKey, scopeEntityId, queryType) {
        const { EnableTrouterIntegration } = this.configProvider().flights;
        const { disable } = this.configProvider().settings.trouterSettings;
        if (!EnableTrouterIntegration || disable) {
            return;
        }
        const subscriptionResult = parseTrouterSubscriptionResponse(trouterViewUpdateKey);
        if (subscriptionResult.success) {
            this.dispatcher.dispatchActionAsync(new TrouterSubscriptionSuccessAction(subscriptionResult.viewUpdateKey, scopeEntityId, queryType));
        } else {
            this.dispatcher.dispatchActionAsync(new TrouterSubscriptionFailedAction(scopeEntityId, queryType, subscriptionResult.errorCode, subscriptionResult.shouldRetry, subscriptionResult.retryAfterSeconds));
        }
    }
    /**
     * Initializes a new instance of the DataPreFetchActionCreator.
     * @param storeLoader: Store loader
     * @param dispatcher: Dispatcher to dispatch actions
     * @param loggers: Logger for telemetry
     * @param configProvider: Config provider
     */ constructor(storeLoader, dispatcher, loggers, configProvider){
        this.storeLoader = storeLoader;
        this.dispatcher = dispatcher;
        this.loggers = loggers;
        this.configProvider = configProvider;
        // Accumulators to store entities across paginated responses
        this.accumulatedPlanTasks = [];
        this.accumulatedBuckets = [];
        this.accumulatedAtmTasks = [];
        this.accumulatedMyDayTasks = [];
    }
}
